<template>
  <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-self-stretch">
    <!-- Desktop manage header (sits above the management panel) -->
    <div
      v-if="showManageGroup"
      class="tw-hidden tw-w-full tw-px-[16px] md:tw-block md:tw-px-[32px] md:tw-pb-[4px]"
    >
      <div
        class="tw-hidden md:tw-mt-0 md:tw-flex md:tw-items-center md:tw-justify-between"
      >
        <div class="tw-flex tw-items-center tw-gap-[16px]">
          <button
            type="button"
            class="tw-font-inter tw-flex tw-h-[44px] tw-items-center tw-justify-center tw-gap-[10px] tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[18px] tw-py-[8px] tw-text-[14px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 lg:tw-text-[16px]"
            @click="closeManageGroup"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                stroke-width="2.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Back to Grouplist
          </button>

          <button
            type="button"
            class="tw-flex tw-items-center tw-gap-2 tw-text-[#00A2E1] hover:tw-text-[#0090C8] focus-visible:tw-rounded-[2px] focus-visible:tw-text-[#0090C8] focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            @click="refreshGroupManagementData"
          >
            <ArrowsClockwise fill-color="#00A2E1" />
            <span
              class="tw-font-inter tw-text-[16px] tw-font-semibold tw-leading-[120%]"
            >
              {{
                studentStore.isRefreshingManageGroup
                  ? 'Refreshing'
                  : 'Refresh grouplist'
              }}
            </span>
          </button>
        </div>

        <button
          type="button"
          class="tw-font-inter tw-flex tw-h-[44px] tw-items-center tw-justify-center tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[18px] tw-py-[8px] tw-text-[14px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 lg:tw-text-[16px]"
          @click="handleManageShare"
        >
          {{ manageShareLabel }}
        </button>
      </div>
    </div>

    <!-- content -->
    <div
      v-if="showManageGroup && targetGroup"
      class="tw-fixed tw-inset-x-0 tw-bottom-0 tw-top-[64px] tw-z-40 tw-overflow-y-auto tw-bg-white md:tw-static md:tw-inset-auto md:tw-z-auto md:tw-w-full md:tw-self-stretch md:tw-overflow-visible"
    >
      <HousemateGroupManage
        :group="targetGroup"
        :student-id="studentStore.studentId"
        :loading-member-student-ids="memberActionLoadingStudentIds"
        :share-url="manageShareUrl"
        @back="closeManageGroup"
        @approve-member="handleApproveMember"
        @decline-member="handleDeclineMember"
        @rename-group="handleRenameGroup"
        @delete-group="handleDeleteGroup"
        @delete-group-immediate="handleImmediateDeleteGroup"
        @leave-group="handleLeaveGroup"
        @remove-member="handleRemoveMember"
      />
    </div>

    <div v-else class="tw-w-full">
      <div
        v-if="!showManageGroup"
        class="tw-mb-4 tw-mt-0 tw-flex tw-w-full tw-items-center tw-justify-start tw-px-[20px] md:tw-my-4 md:tw-px-[32px]"
      >
        <button
          type="button"
          class="tw-flex tw-items-center tw-gap-2 tw-text-[#00A2E1] hover:tw-text-[#0090C8] focus-visible:tw-rounded-[2px] focus-visible:tw-text-[#0090C8] focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 md:-tw-ml-[20px]"
          @click="refreshGroupManagementData"
        >
          <ArrowsClockwise fill-color="#00A2E1" />
          <span
            class="tw-font-inter tw-text-[14px] tw-font-semibold tw-leading-[120%] md:tw-text-[16px]"
          >
            {{
              studentStore.isRefreshingManageGroup
                ? 'Refreshing'
                : 'Refresh grouplist'
            }}
          </span>
        </button>
      </div>

      <div v-if="showGrouplistSkeleton">
        <h3 class="tw-sr-only">{{ manageGroupName }}</h3>

        <div class="row uh-housemate-group-tiles">
          <div
            v-for="n in 6"
            :key="`grouplist-skeleton-${n}`"
            class="col-12 col-sm-6 col-xl-4 property-listing-column tw-mb-[18px] md:tw-mb-[36px]"
          >
            <div
              class="tw-overflow-hidden tw-rounded-[16px] tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            >
              <div
                class="skeleton tw-aspect-[4/3] tw-w-full tw-bg-gray-100"
              ></div>

              <div class="tw-p-[16px]">
                <div class="tw-mb-[16px] tw-flex tw-gap-[8px]">
                  <div
                    class="skeleton tw-h-[27px] tw-w-[130px] tw-rounded-full tw-bg-gray-100"
                  ></div>
                  <div
                    class="skeleton tw-h-[27px] tw-w-[110px] tw-rounded-full tw-bg-gray-100"
                  ></div>
                </div>

                <div
                  class="skeleton tw-mb-[8px] tw-h-[28px] tw-w-3/4 tw-rounded tw-bg-gray-100"
                ></div>
                <div
                  class="skeleton tw-mb-[16px] tw-h-[20px] tw-w-2/3 tw-rounded tw-bg-gray-100"
                ></div>
                <div
                  class="skeleton tw-h-[24px] tw-w-1/2 tw-rounded tw-bg-gray-100"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="grouplistPropertiesCount === 0"
        class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-px-[16px] tw-pb-[120px] tw-pt-[16px]"
      >
        <div
          class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center"
        >
          <div class="tw-aspect-[400/273] tw-w-full tw-max-w-[400px]">
            <img
              :src="cdnAsset('images/student/groups-empty.png')"
              alt=""
              loading="lazy"
              decoding="async"
              class="tw-block tw-h-full tw-w-full tw-object-contain"
            />
          </div>

          <div
            class="tw-font-inter tw-line-clamp-2 tw-self-stretch tw-overflow-hidden tw-text-ellipsis tw-py-[8px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
          >
            Get started by adding some properties
          </div>

          <div
            class="tw-font-inter tw-pb-[24px] tw-text-center tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
          >
            Add properties to collaborate on a shortlist and explore options
            together! Stay in the know and make important decisions as a team.
          </div>
        </div>

        <div
          class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-start tw-gap-[16px]"
        >
          <button
            type="button"
            class="tw-font-inter tw-flex tw-h-[50px] tw-min-w-[343px] tw-items-center tw-justify-center tw-gap-2 tw-self-stretch tw-rounded-[30px] tw-bg-[#00A2E1] tw-px-6 tw-py-2 tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-white hover:tw-bg-uhblue-700 focus-visible:tw-bg-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            @click="studentStore.handleReturnToSearchPage()"
          >
            <MagnifyingGlass />
            Back to search results
          </button>
        </div>
      </div>

      <div v-else>
        <h3 class="tw-sr-only">{{ manageGroupName }}</h3>

        <div class="row uh-housemate-group-tiles max-[768px]:tw-mx-0">
          <div
            v-for="(property, index) in displayedGrouplistProperties"
            :key="
              property.id
                ? `grouplist-property-${property.id}`
                : `grouplist-property-${index}`
            "
            class="col-12 col-sm-6 col-xl-4 property-listing-column tw-mb-[18px] tw-hidden md:tw-mb-[36px] md:tw-flex"
          >
            <PropertyListingBackup2024
              :property="property"
              :iteration="index + 1"
              :enable-housemate-groups="enableHousemateGroups"
              :enable-updated-property-tile="enableUpdatedPropertyTile"
              :housemate-group-active="true"
              :housemate-group-id="targetGroupId"
              :show-housemate-vote-placeholders="true"
              @housemate-group-vote="handleHousemateGroupVote"
            />
          </div>

          <div class="tw-block tw-w-full tw-px-[20px] md:tw-hidden">
            <div
              v-for="(property, index) in displayedGrouplistProperties"
              :key="
                property.id
                  ? `grouplist-property-mobile-${property.id}`
                  : `grouplist-property-mobile-${index}`
              "
              class="tw-w-full tw-border-b tw-border-gray-200 tw-py-[24px] first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0"
            >
              <MobileGroupShortlistCard
                :property="property"
                :housemate-group-active="true"
                :housemate-group-id="targetGroupId"
                @housemate-group-vote="handleHousemateGroupVote"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!showManageGroup"
      id="sticky-container"
      class="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-50 tw-flex tw-w-full tw-items-center tw-justify-between tw-border-t tw-border-[#D1D5DB] tw-bg-white tw-p-[16px] tw-transition-all tw-duration-300 tw-ease-out md:tw-pointer-events-auto md:tw-sticky md:tw-inset-x-auto md:tw-bottom-[24px] md:tw-mx-auto md:tw-mb-[44px] md:tw-max-w-[586px] md:tw-translate-y-0 md:tw-rounded-full md:tw-border-gray-200 md:tw-opacity-100 md:tw-shadow-lg"
    >
      <div class="tw-flex tw-w-full tw-items-center tw-gap-[16px]">
        <div ref="floatingMenuOptionsWrapper" class="tw-relative">
          <button
            type="button"
            class="tw-flex tw-h-[50px] tw-w-[50px] tw-items-center tw-justify-center tw-rounded-[25px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-p-[13px] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-border-uhblue-700 focus-visible:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            @click="showMenuOptions = !showMenuOptions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 10.5625C12.7939 10.5625 13.4375 11.2061 13.4375 12C13.4375 12.7939 12.7939 13.4375 12 13.4375C11.2061 13.4375 10.5625 12.7939 10.5625 12C10.5625 11.2061 11.2061 10.5625 12 10.5625Z"
                fill="#00A2E1"
                stroke="#00A2E1"
                stroke-width="0.125"
              />
              <path
                d="M5.625 10.5625C6.41891 10.5625 7.0625 11.2061 7.0625 12C7.0625 12.7939 6.41891 13.4375 5.625 13.4375C4.83109 13.4375 4.1875 12.7939 4.1875 12C4.1875 11.2061 4.83109 10.5625 5.625 10.5625Z"
                fill="#00A2E1"
                stroke="#00A2E1"
                stroke-width="0.125"
              />
              <path
                d="M18.375 10.5625C19.1689 10.5625 19.8125 11.2061 19.8125 12C19.8125 12.7939 19.1689 13.4375 18.375 13.4375C17.5811 13.4375 16.9375 12.7939 16.9375 12C16.9375 11.2061 17.5811 10.5625 18.375 10.5625Z"
                fill="#00A2E1"
                stroke="#00A2E1"
                stroke-width="0.125"
              />
            </svg>
          </button>

          <span
            v-if="pendingJoinRequestsCount > 0"
            class="tw-absolute tw-right-[5px] tw-top-[3px] tw-flex tw-h-4 tw-w-4 tw--translate-y-1/3 tw-translate-x-1/3 tw-transform tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white tw-bg-orange-500 tw-text-[11px] tw-font-semibold tw-leading-[16px] tw-tracking-tight tw-text-white"
          >
            {{ pendingJoinRequestsCount > 9 ? '9+' : pendingJoinRequestsCount }}
          </span>

          <GroupsFloatingMenuOptions
            :show-menu-options="showMenuOptions"
            :pending-join-requests-count="pendingJoinRequestsCount"
            :share-url="manageShareUrl"
            :can-rename-group="
              studentStore.targetHousemateGroup?.status === 'APPROVED'
            "
            :can-delete-group="canDeleteGroup"
            @open-context="openContext"
            @confirm-delete-group="confirmDeleteGroupFromMenu"
            @close-floating-menu-options="showMenuOptions = false"
          />
        </div>

        <div class="tw-flex tw-h-[50px] tw-flex-1 tw-gap-4">
          <button
            type="button"
            class="tw-font-inter tw-group tw-hidden tw-h-[50px] tw-flex-1 tw-items-center tw-justify-center tw-gap-2 tw-self-stretch tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-6 tw-py-2 tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-border-uhblue-700 focus-visible:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 md:tw-flex"
            @click="handleManageShare"
          >
            <ShareNetwork
              stroke-color="currentColor"
              class="tw-text-[#3B4143] group-hover:tw-text-uhblue-700"
            />
            {{ floatingBarShareLabel }}
          </button>

          <button
            type="button"
            :disabled="isEnquireToUpvotedDisabled"
            :aria-disabled="isEnquireToUpvotedDisabled"
            :class="[
              'tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-[8px] tw-self-stretch tw-rounded-[30px] tw-bg-[#F48831] tw-px-[24px] tw-py-[8px] tw-text-white hover:tw-bg-[#E16B0C] focus-visible:tw-bg-[#E16B0C] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-bg-[#efefef] disabled:tw-text-[#3B4143] disabled:hover:tw-bg-[#efefef] disabled:hover:tw-text-[#3B4143]',
              {
                'tw-cursor-not-allowed': isEnquireToUpvotedDisabled,
                'tw-opacity-90': isEnquireToUpvotedDisabled,
              },
            ]"
            @click="handleEnquireToUpvoted"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M15.75 3.375H2.25C2.10082 3.375 1.95774 3.43426 1.85225 3.53975C1.74676 3.64524 1.6875 3.78832 1.6875 3.9375V13.5C1.6875 13.7984 1.80603 14.0845 2.017 14.2955C2.22798 14.5065 2.51413 14.625 2.8125 14.625H15.1875C15.4859 14.625 15.772 14.5065 15.983 14.2955C16.194 14.0845 16.3125 13.7984 16.3125 13.5V3.9375C16.3125 3.78832 16.2532 3.64524 16.1477 3.53975C16.0423 3.43426 15.8992 3.375 15.75 3.375ZM9 9.36211L3.69633 4.5H14.3037L9 9.36211ZM6.94055 9L2.8125 12.7835V5.21648L6.94055 9ZM7.77305 9.76289L8.6168 10.5398C8.72057 10.6351 8.85632 10.688 8.99719 10.688C9.13806 10.688 9.2738 10.6351 9.37758 10.5398L10.2213 9.76289L14.2995 13.5H3.69633L7.77305 9.76289ZM11.0595 9L15.1875 5.21578V12.7842L11.0595 9Z"
                :fill="isEnquireToUpvotedDisabled ? '#3B4143' : 'white'"
              />
            </svg>
            <span
              class="tw-font-inter tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%]"
            >
              Enquire to upvoted
            </span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="!showManageGroup" class="tw-h-[82px] md:tw-hidden"></div>
    <!-- Housemate Group List Modal -->
    <HousemateGroupModal
      v-if="showGroupListModal"
      :show="showGroupListModal"
      dynamic-body-class="md:tw-py-[24px] md:tw-px-[24px] tw-px-[24px] tw-py-[16px]"
      @close-modal="closeGroupListModal"
    >
      <template #header> Choose Grouplist </template>

      <template #body>
        <Group
          v-for="groupData in studentStore.studentHousemateGroups"
          :key="groupData?.id"
          :group-data="groupData"
          @close-group-list-modal="closeGroupListModal"
        ></Group>
      </template>

      <template #actions>
        <button
          type="button"
          class="tw-font-inter tw-relative tw-cursor-pointer tw-border-none tw-bg-none tw-p-0 tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-[#3b4143] hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-outline focus-visible:tw-outline-[2px] focus-visible:tw-outline-offset-[2px] focus-visible:tw-outline-[#3b414380] focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
          @click="closeGroupListModal"
        >
          Cancel
        </button>

        <div class="tw-relative tw-inline-flex">
          <Tooltip
            :show-tool-tip="
              showToolTip && !!studentStore.hasReachedCreateGroupLimit
            "
            :can-close-tool-tip="false"
          >
            <template #body> Limit reached </template>
          </Tooltip>

          <div @click="handleShowToolTip" @mouseleave="handleCloseToolTip">
            <button
              type="button"
              :class="{
                'tw-pointer-events-none':
                  !!studentStore.hasReachedCreateGroupLimit,
              }"
              class="tw-group tw-flex tw-h-[50px] tw-max-h-[50px] tw-min-w-[189px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-[30px] tw-border tw-border-[#d1d5db] tw-bg-white tw-px-6 tw-py-2 tw-text-[14px] tw-font-semibold hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-border-uhblue-700 focus-visible:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 active:tw-opacity-90 lg:tw-text-[16px]"
              :disabled="disableHouseMateGroupCreation"
              @click="
                showGroupListModal = false;
                showGroupCreateModal = true;
              "
            >
              <!-- SVG-->
              <Users class="tw-text-[#3B4143] group-hover:tw-text-uhblue-700" />
              Create a group
            </button>
          </div>
        </div>
      </template>
    </HousemateGroupModal>

    <!-- Housemate Group Create Modal -->
    <HousemateGroupModal
      v-if="showGroupCreateModal"
      :show="showGroupCreateModal"
      @close-modal="closeGroupCreateModal"
    >
      <template #header> Create a group </template>

      <template #body>
        <div
          class="tw-flex tw-flex-col tw-items-start tw-gap-6 tw-self-stretch tw-bg-white"
        >
          <div
            class="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-self-stretch"
          >
            <div class="tw-flex tw-flex-col tw-gap-1 tw-self-stretch">
              <label
                for="groupName"
                class="tw-font-inter tw-self-stretch tw-text-base tw-font-normal tw-leading-[150%] tw-text-[#3B4143]"
              >
                Group name
              </label>
              <input
                id="groupName"
                ref="groupNameInput"
                v-model="inputGroupName"
                type="text"
                placeholder="Enter group name (optional)"
                class="tw-font-inter tw-flex tw-h-[50px] tw-items-center tw-gap-[10px] tw-self-stretch tw-rounded-lg tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-4 tw-py-2 tw-text-base md:tw-w-[552px]"
                @input="studentStore.clearHousemateGroupError()"
              />
            </div>
          </div>

          <HousemateGroupLocationField
            v-model="selectedLocation"
            :cities="cities"
            :universities="allUniversities"
            placeholder="Select city or university from list"
            @update:modelValue="studentStore.clearHousemateGroupError()"
          />

          <div
            v-if="studentStore.hasGroupCreateError"
            class="tw-flex tw-font-bold tw-text-red-600"
          >
            <ul class="text-red-500 tw-mb-0">
              <li
                v-for="err in studentStore.groupCreateErrorMsg"
                :key="err"
                class="tw-list-disc"
              >
                {{ err }}
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- Actions -->
      <template #actions>
        <button
          type="button"
          class="tw-font-inter tw-cursor-pointer tw-border-none tw-bg-none tw-p-0 tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-[#3b4143] hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-outline focus-visible:tw-outline-[2px] focus-visible:tw-outline-offset-[2px] focus-visible:tw-outline-[#3b414380] focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
          @click="closeGroupCreateModal"
        >
          Cancel
        </button>

        <div class="tw-relative tw-inline-flex">
          <Tooltip
            :show-tool-tip="
              showToolTip && !!studentStore.hasReachedCreateGroupLimit
            "
            :can-close-tool-tip="false"
          >
            <template #body> Limit reached </template>
          </Tooltip>

          <div @click="handleShowToolTip" @mouseleave="handleCloseToolTip">
            <button
              type="button"
              :class="{
                'tw-text-[#3B4143]': disableHouseMateGroupCreation,
                'tw-text-white': !disableHouseMateGroupCreation,
                'tw-pointer-events-none':
                  !!studentStore.hasReachedCreateGroupLimit,
                'focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2':
                  !disableHouseMateGroupCreation,
              }"
              :disabled="disableHouseMateGroupCreation"
              class="tw-flex tw-h-[50px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-[30px] tw-border-none tw-bg-[#f48831] tw-px-6 tw-py-2 tw-text-[14px] tw-font-semibold active:tw-opacity-90 enabled:hover:tw-bg-[#BF5209] lg:tw-text-[16px]"
              @click="createHousemateGroup"
            >
              <!-- SVG -->
              <Users
                :fill-color="disableHouseMateGroupCreation ? '#3B4143' : '#FFF'"
              />
              Create group
            </button>
          </div>
        </div>
      </template>
    </HousemateGroupModal>

    <!-- Housemate Group Rename Modal -->
    <HousemateGroupModal
      v-if="showGroupRenameModal"
      :show="showGroupRenameModal"
      @close-modal="closeGroupRenameModal"
    >
      <template #header> Rename group </template>

      <template #body>
        <div
          class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-4 tw-self-stretch tw-bg-white"
        >
          <div class="tw-flex tw-w-full tw-flex-col tw-gap-1 tw-self-stretch">
            <label
              for="renameGroupName"
              class="tw-font-inter tw-self-stretch tw-text-base tw-font-normal tw-leading-[150%] tw-text-[#3B4143]"
            >
              Group name
            </label>
            <input
              id="renameGroupName"
              ref="renameGroupNameInput"
              v-model="renameGroupName"
              type="text"
              placeholder="Enter group name"
              class="tw-font-inter tw-flex tw-h-[50px] tw-items-center tw-gap-[10px] tw-self-stretch tw-rounded-lg tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-4 tw-py-2 tw-text-base md:tw-w-[552px]"
              :disabled="isManagingGroup"
              @input="studentStore.clearManageError()"
            />
          </div>

          <div
            v-if="studentStore.hasGroupManageError"
            class="tw-flex tw-font-bold tw-text-red-600"
          >
            {{ studentStore.groupManageErrorMsg }}
          </div>
        </div>
      </template>

      <template #actions>
        <button
          type="button"
          class="uh-hmg-modal-cancel tw-font-inter tw-cursor-pointer tw-border-none tw-bg-none tw-p-0 tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-[#3b4143] focus-visible:tw-outline focus-visible:tw-outline-[2px] focus-visible:tw-outline-offset-[2px] focus-visible:tw-outline-[#3b414380]"
          :disabled="isManagingGroup"
          @click="closeGroupRenameModal"
        >
          Cancel
        </button>

        <button
          type="button"
          class="tw-font-inter tw-flex tw-h-[50px] tw-min-w-[160px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[30px] tw-border-none tw-bg-[#f48831] tw-px-6 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-white active:tw-opacity-90"
          :disabled="isManagingGroup"
          @click="submitGroupRename"
        >
          Save
        </button>
      </template>
    </HousemateGroupModal>

    <!-- Housemate Group Delete Modal -->
    <HousemateGroupModal
      v-if="showGroupDeleteModal"
      :show="showGroupDeleteModal"
      @close-modal="closeGroupDeleteModal"
    >
      <template #header> Delete group </template>

      <template #body>
        <div
          class="tw-font-inter tw-text-[16px] tw-leading-[150%] tw-text-[#3B4143]"
        >
          This will permanently delete the group and its shortlist. This can’t
          be undone.
        </div>

        <div
          v-if="studentStore.hasGroupManageError"
          class="tw-flex tw-w-full tw-font-bold tw-text-red-600"
        >
          {{ studentStore.groupManageErrorMsg }}
        </div>
      </template>

      <template #actions>
        <button
          type="button"
          class="uh-hmg-modal-cancel tw-font-inter tw-cursor-pointer tw-border-none tw-bg-none tw-p-0 tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-[#3b4143] focus-visible:tw-outline focus-visible:tw-outline-[2px] focus-visible:tw-outline-offset-[2px] focus-visible:tw-outline-[#3b414380]"
          :disabled="isManagingGroup"
          @click="closeGroupDeleteModal"
        >
          Cancel
        </button>

        <button
          type="button"
          class="tw-font-inter tw-flex tw-h-[50px] tw-min-w-[160px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[30px] tw-border-none tw-bg-[#E7000B] tw-px-6 tw-py-2 tw-text-[14px] tw-font-semibold tw-text-white active:tw-opacity-90"
          :disabled="isManagingGroup"
          @click="confirmGroupDelete"
        >
          Delete
        </button>
      </template>
    </HousemateGroupModal>
  </div>
</template>
<script>
import { useStudentStore } from '@js/stores/student/student';
import GroupsFloatingMenuOptions from '@components/Student/Menu/GroupsFloatingMenuOptions.vue';
import HousemateGroupModal from '@components/Student/Modal/HousemateGroupModal.vue';
import HousemateGroupLocationField from '@components/Student/HousemateGroupLocationDropdownField.vue';
import Users from '@components/Student/SVG/Users.vue';
import ArrowsClockwise from '@components/Student/SVG/ArrowsClockwise.vue';
import ShareNetwork from '@components/Student/SVG/ShareNetwork.vue';
import MagnifyingGlass from '@components/Student/SVG/MagnifyingGlass.vue';
import Group from '@components/Student/Group.vue';
import Tooltip from '@components/Student/Tooltip/Tooltip.vue';
import HousemateGroupManage from '@components/Student/HousemateGroupManage.vue';
import PropertyListingBackup2024 from '@components/PropertyListingBackup2024.vue';
import MobileGroupShortlistCard from '@components/Student/PropertyCard/MobileGroupShortlistCard.vue';

export default {
  name: 'HousemateGroupDetail',
  components: {
    HousemateGroupLocationField,
    Tooltip,
    Group,
    Users,
    MagnifyingGlass,
    ArrowsClockwise,
    ShareNetwork,
    HousemateGroupModal,
    GroupsFloatingMenuOptions,
    HousemateGroupManage,
    PropertyListingBackup2024,
    MobileGroupShortlistCard,
  },
  props: {
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    enableUpdatedPropertyTile: {
      type: Boolean,
      default: false,
    },
    forceSkeleton: {
      type: Boolean,
      default: false,
    },
    sortKey: {
      type: String,
      default: 'relevance',
    },
    page: {
      default: 1,
      type: [Number, String],
    },
    paginate: {
      default: 0,
      type: Number,
    },
    cities: {
      type: Array,
      default: () => [],
    },
    allUniversities: {
      type: Array,
      default: () => [],
    },
    universities: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const studentStore = useStudentStore();

    return {
      studentStore,
      showMenuOptions: false,
      floatingMenuListenersActive: false,
      showGroupListModal: false,
      showGroupCreateModal: false,
      showManageGroup: false,
      showGroupRenameModal: false,
      showGroupDeleteModal: false,
      renameGroupName: '',
      isManagingGroup: false,
      memberActionLoadingStudentIds: [],
      inputGroupName: studentStore.nextGroupName,
      showToolTip: false,
      scrollTimeout: null,
      scrollStartY: null,
      enquiryModalChangeListener: null,
      manageShareLabel: 'Share link to invite housemates',
      manageShareResetTimeoutId: null,
      isMobileManageScrollLocked: false,
      previousHtmlOverflow: null,
      previousBodyOverflow: null,
      isCreatingNewHousemateGroup: false,
      selectedLocation: null,
      showToolTipTimeout: null,
      lastAutoManageGroupId: null,
    };
  },
  computed: {
    targetGroup() {
      return this.studentStore.targetHousemateGroup ?? null;
    },
    manageGroupName() {
      return (
        this.studentStore.targetHousemateGroup?.housemate_group_name ?? 'Group'
      );
    },
    manageShareUrl() {
      return (
        this.studentStore.targetHousemateGroup
          ?.housemate_group_open_invite_link ?? null
      );
    },
    disableHouseMateGroupCreation() {
      return (
        this.studentStore.hasReachedCreateGroupLimit ||
        this.isCreatingNewHousemateGroup
      );
    },
    targetGroupMembers() {
      if (Array.isArray(this.targetGroup?.members)) {
        return this.targetGroup.members;
      }

      if (Array.isArray(this.targetGroup?.housemate_group_members)) {
        return this.targetGroup.housemate_group_members;
      }

      return [];
    },
    canDeleteGroup() {
      // Mirrors backend rule: only delete when there is 1 person in group.
      // Treat pending join requests as additional participants.
      const members = this.targetGroupMembers;

      const pendingCount = members.filter(
        (member) => member?.status === 'PENDING'
      ).length;
      const approvedCount = members.filter(
        (member) => member?.status === 'APPROVED'
      ).length;

      return (
        this.studentStore.targetHousemateGroup?.status === 'APPROVED' &&
        approvedCount === 1 &&
        pendingCount === 0
      );
    },
    pendingJoinRequestsCount() {
      const members = this.targetGroupMembers;

      return members.filter((member) => member.status === 'PENDING').length;
    },

    approvedMemberCount() {
      const members = this.targetGroupMembers;

      return members.filter((member) => member?.status === 'APPROVED').length;
    },

    enquirableUpvotedProperties() {
      const approvedCount = Number(this.approvedMemberCount || 0);
      const shortlist = Array.isArray(
        this.studentStore.housemateGroupShortlists
      )
        ? this.studentStore.housemateGroupShortlists
        : [];

      if (approvedCount <= 0 || shortlist.length === 0) {
        return [];
      }

      return shortlist.filter((property) => {
        const yesCount = Number(property?.groupVoteYesCount ?? 0);
        const noCount = Number(property?.groupVoteNoCount ?? 0);
        const hasGroupEnquiry = Boolean(
          property?.housemateGroupLastEnquiredAt ??
            property?.housemate_group_last_enquired_at
        );

        return yesCount > noCount && !hasGroupEnquiry;
      });
    },

    isEnquireToUpvotedDisabled() {
      return this.enquirableUpvotedProperties.length === 0;
    },

    grouplistPropertiesCount() {
      const explicitCount = this.studentStore.housemateGroupShortlistCount;

      if (Number.isFinite(explicitCount)) {
        return explicitCount;
      }

      return Array.isArray(this.studentStore.housemateGroupShortlists)
        ? this.studentStore.housemateGroupShortlists.length
        : 0;
    },
    displayedGrouplistProperties() {
      const shortlist = Array.isArray(this.orderedGrouplistProperties)
        ? this.orderedGrouplistProperties
        : [];

      const perPage = Number(this.paginate || 0);
      if (perPage <= 0) {
        return shortlist;
      }

      const currentPage = Math.max(1, Number(this.page) || 1);
      const start = (currentPage - 1) * perPage;

      return shortlist.slice(start, start + perPage);
    },

    targetGroupId() {
      return (
        this.targetGroup?.housemate_group_id ??
        this.targetGroup?.housemate_group?.id ??
        this.studentStore.targetHousemateGroup?.housemate_group_id ??
        this.targetGroup?.id ??
        null
      );
    },
    orderedGrouplistProperties() {
      const list = Array.isArray(this.studentStore.housemateGroupShortlists)
        ? [...this.studentStore.housemateGroupShortlists]
        : [];

      const sortKey = String(this.sortKey || 'relevance');

      if (sortKey === 'upvotes') {
        return this.studentStore.sortHousemateGroupPropertiesByVotes(list);
      }

      if (sortKey === 'price-asc') {
        return list.sort((a, b) => this.getPrice(a) - this.getPrice(b));
      }

      if (sortKey === 'price-desc') {
        return list.sort((a, b) => this.getPrice(b) - this.getPrice(a));
      }

      return list;
    },

    expectedGrouplistPropertiesCount() {
      const groupId = this.targetGroupId;
      if (!groupId) {
        return null;
      }

      const counts = this.studentStore.housemateGroupShortlistedCountByGroupId;
      if (!counts || typeof counts !== 'object') {
        return null;
      }

      return counts[String(groupId)] ?? 0;
    },

    showGrouplistSkeleton() {
      if (this.forceSkeleton) {
        return true;
      }

      if (this.grouplistPropertiesCount > 0) {
        return false;
      }

      // If we've already loaded the shortlists for this group, trust the real
      // fetched count (even if the property-id mapping cache is momentarily stale).
      if (
        !this.studentStore.housemateGroupShortlistsLoading &&
        this.targetGroupId &&
        String(this.studentStore.housemateGroupShortlistsLoadedGroupId) ===
          String(this.targetGroupId)
      ) {
        return false;
      }

      if (this.studentStore.housemateGroupShortlistsLoading) {
        return true;
      }

      // If we haven't fetched the mapping yet, avoid briefly showing empty state.
      if (!this.studentStore.housemateGroupShortlistedByPropertyIdFetched) {
        return true;
      }

      const expected = Number(this.expectedGrouplistPropertiesCount ?? 0);
      return Number.isFinite(expected) && expected > 0;
    },
    floatingBarShareLabel() {
      if (this.manageShareLabel === 'Link copied') {
        return 'Link copied';
      }

      if (this.manageShareLabel === 'Error!') {
        return 'Error!';
      }

      return 'Invite housemates';
    },
  },
  watch: {
    showMenuOptions(isOpen) {
      this.toggleFloatingMenuListeners(!!isOpen);
    },
    showManageGroup(isVisible) {
      this.studentStore.isHousemateGroupManageMode = !!isVisible;

      this.setMobileManageScrollLock(!!isVisible);
    },
    showGroupCreateModal(isVisible) {
      if (isVisible) {
        this.$nextTick(() => {
          this.$refs.groupNameInput?.focus();
        });
      }
    },
    showGroupRenameModal(isVisible) {
      if (isVisible) {
        this.$nextTick(() => {
          this.$refs.renameGroupNameInput?.focus();
        });
      }
    },
    orderedGrouplistProperties: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.rehydrateHousemateEnquiryModalSummary();
        });
      },
    },
    targetGroupId(newGroupId) {
      if (!newGroupId) {
        this.lastAutoManageGroupId = null;
        this.showManageGroup = false;
        return;
      }

      this.syncManageModeForActiveGroup({ force: true });
    },
  },
  mounted() {
    this.studentStore.setTargetHousemateGroup();
    this.syncManageModeForActiveGroup({ force: true });

    this.bindEnquiryModalResetListener();

    this.$nextTick(() => {
      this.rehydrateHousemateEnquiryModalSummary();
    });

    if (!this.studentStore.housemateGroupShortlistedByPropertyIdFetched) {
      this.studentStore
        .getHousemateGroupShortlistedPropertyIds?.()
        .catch(() => null);
    }
  },
  beforeUnmount() {
    this.toggleFloatingMenuListeners(false);
    this.studentStore.isHousemateGroupManageMode = false;

    this.unbindEnquiryModalResetListener();

    this.setMobileManageScrollLock(false);

    if (this.manageShareResetTimeoutId) {
      clearTimeout(this.manageShareResetTimeoutId);
      this.manageShareResetTimeoutId = null;
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  },
  methods: {
    syncManageModeForActiveGroup({ force = false } = {}) {
      const group = this.targetGroup;
      const groupId = this.targetGroupId;

      if (!group || !groupId) {
        this.showManageGroup = false;
        this.lastAutoManageGroupId = null;
        return;
      }

      const normalizedGroupId = String(groupId);

      if (!force && this.lastAutoManageGroupId === normalizedGroupId) {
        return;
      }

      this.showManageGroup =
        this.studentStore.shouldOpenHousemateGroupManage(group);
      this.lastAutoManageGroupId = normalizedGroupId;
    },
    getPersistedHousemateEnquiryPropertyIds() {
      if (typeof document === 'undefined') {
        return [];
      }

      const contextInput = document.getElementById('enquiry_context');
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      const isHousemateContext =
        contextInput instanceof HTMLInputElement &&
        contextInput.value === 'housemate_group';

      if (
        !isHousemateContext ||
        !(propertyIdsInput instanceof HTMLInputElement)
      ) {
        return [];
      }

      return propertyIdsInput.value
        .split(',')
        .map((id) => String(id || '').trim())
        .filter(Boolean);
    },
    getPersistedHousemateEnquiryProperties() {
      const propertyIds = this.getPersistedHousemateEnquiryPropertyIds();

      if (propertyIds.length === 0) {
        return [];
      }

      const properties = Array.isArray(this.orderedGrouplistProperties)
        ? this.orderedGrouplistProperties
        : [];

      return properties.filter((property) => {
        const propertyIdentifier = this.getPropertyIdentifier(property);

        return propertyIdentifier && propertyIds.includes(propertyIdentifier);
      });
    },
    getPropertyIdentifier(property) {
      const candidateIds = [
        property?.id,
        property?.propertyId,
        property?.property_id,
      ];

      for (const candidate of candidateIds) {
        const value = String(candidate ?? '').trim();

        if (value) {
          return value;
        }
      }

      return '';
    },
    rehydrateHousemateEnquiryModalSummary() {
      if (typeof document === 'undefined') {
        return;
      }

      const enquiryModalToggle = document.getElementById('enquiry_modal');
      const hasOpenModal =
        enquiryModalToggle instanceof HTMLInputElement &&
        enquiryModalToggle.checked;

      if (!hasOpenModal) {
        return;
      }

      const scopedProperties = this.getPersistedHousemateEnquiryProperties();

      if (scopedProperties.length === 0) {
        return;
      }

      this.setEnquiryModalSummary(scopedProperties);
    },
    getPrice(property) {
      const candidates = [
        property?.cheapestRoomPrice,
        property?.price,
        property?.cheapest_room_price,
      ];

      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value)) {
          return value;
        }
      }

      return Number.MAX_SAFE_INTEGER;
    },
    openEnquiryModal(fallbackUrl = '/student/shortlist?open-enquiry-modal=1') {
      if (typeof document !== 'undefined') {
        const enquiryModalToggle = document.getElementById('enquiry_modal');

        if (
          enquiryModalToggle &&
          enquiryModalToggle instanceof HTMLInputElement &&
          enquiryModalToggle.type === 'checkbox'
        ) {
          enquiryModalToggle.checked = true;
          return true;
        }
      }

      if (typeof window !== 'undefined') {
        window.location.assign(fallbackUrl);
      }

      return false;
    },
    bindEnquiryModalResetListener() {
      if (typeof document === 'undefined') {
        return;
      }

      const enquiryModalToggle = document.getElementById('enquiry_modal');

      if (
        !(enquiryModalToggle instanceof HTMLInputElement) ||
        enquiryModalToggle.type !== 'checkbox'
      ) {
        return;
      }

      this.enquiryModalChangeListener = (event) => {
        const modalToggle = event?.target;

        if (!(modalToggle instanceof HTMLInputElement) || modalToggle.checked) {
          return;
        }

        this.handleEnquiryModalClosed();
      };

      enquiryModalToggle.addEventListener(
        'change',
        this.enquiryModalChangeListener
      );
    },
    unbindEnquiryModalResetListener() {
      if (typeof document === 'undefined') {
        return;
      }

      if (typeof this.enquiryModalChangeListener !== 'function') {
        return;
      }

      const enquiryModalToggle = document.getElementById('enquiry_modal');

      if (enquiryModalToggle instanceof HTMLInputElement) {
        enquiryModalToggle.removeEventListener(
          'change',
          this.enquiryModalChangeListener
        );
      }

      this.enquiryModalChangeListener = null;
    },
    handleEnquiryModalClosed() {
      this.showMenuOptions = false;

      this.setEnquiryModalContext({
        context: 'shortlist',
        housemateGroupId: null,
        propertyIds: [],
      });

      this.setEnquiryModalSummary([]);

      if (
        this.targetGroupId &&
        typeof this.studentStore.getHousemateGroupShortlists === 'function'
      ) {
        this.studentStore
          .getHousemateGroupShortlists(this.targetGroupId, {
            showLoading: false,
          })
          .catch(() => null);
      }
    },
    setEnquiryModalContext({
      context = 'shortlist',
      housemateGroupId = null,
      propertyIds = [],
    } = {}) {
      if (typeof document === 'undefined') {
        return;
      }

      const contextInput = document.getElementById('enquiry_context');
      const groupIdInput = document.getElementById(
        'enquiry_housemate_group_id'
      );
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      if (contextInput instanceof HTMLInputElement) {
        contextInput.value = context || 'shortlist';
      }

      if (groupIdInput instanceof HTMLInputElement) {
        groupIdInput.value = housemateGroupId ? String(housemateGroupId) : '';
      }

      if (propertyIdsInput instanceof HTMLInputElement) {
        const normalised = Array.isArray(propertyIds)
          ? propertyIds
              .map((id) => String(id || '').trim())
              .filter((id) => id.length > 0)
          : [];

        propertyIdsInput.value = normalised.join(',');
      }
    },
    setEnquiryModalSummary(enquiryProperties = []) {
      if (typeof document === 'undefined') {
        return;
      }

      const countEl = document.getElementById(
        'enquiry_multi_property_count_text'
      );
      const locationRowEl = document.getElementById(
        'enquiry_multi_location_row'
      );
      const citiesEl = document.getElementById('enquiry_multi_cities_text');
      const lowestPriceEl = document.getElementById(
        'enquiry_multi_lowest_price_text'
      );

      if (!countEl && !locationRowEl && !citiesEl && !lowestPriceEl) {
        return;
      }

      const properties = Array.isArray(enquiryProperties)
        ? enquiryProperties
        : [];

      const enquiryCount = properties.length;

      const lowestPrice = properties.reduce((carry, property) => {
        const price = this.getPrice(property);

        if (!Number.isFinite(price) || price === Number.MAX_SAFE_INTEGER) {
          return carry;
        }

        if (!Number.isFinite(carry)) {
          return price;
        }

        return Math.min(carry, price);
      }, NaN);

      const citySummary = properties
        .map((property) => this.getPropertyCityLabel(property))
        .map((city) => String(city || '').trim())
        .filter((city, index, arr) => city && arr.indexOf(city) === index)
        .join(', ');

      const firstProperty = properties[0] || null;
      const singlePropertyLocation = firstProperty
        ? String(
            firstProperty?.addressShort ||
              firstProperty?.address ||
              firstProperty?.fullAddress ||
              [
                firstProperty?.area?.name,
                firstProperty?.area?.city?.name,
                firstProperty?.city,
              ]
                .filter(Boolean)
                .join(', ')
          ).trim()
        : '';

      const locationText =
        enquiryCount === 1 ? singlePropertyLocation : citySummary;

      if (countEl) {
        countEl.textContent = enquiryCount
          ? `${enquiryCount} ${enquiryCount === 1 ? 'Property' : 'Properties'}`
          : '';
      }

      if (citiesEl) {
        citiesEl.textContent = locationText;
      }

      if (locationRowEl) {
        const shouldShowLocation = enquiryCount === 1 && !!locationText;
        locationRowEl.classList.toggle('tw-hidden', !shouldShowLocation);
      }

      if (lowestPriceEl) {
        lowestPriceEl.textContent = Number.isFinite(lowestPrice)
          ? String(Math.round(lowestPrice))
          : '';
      }

      this.setEnquiryModalThumbnails(properties);
    },
    getPropertyCityLabel(property) {
      if (!property) {
        return '';
      }

      const explicitCity =
        property?.city ||
        property?.cityName ||
        property?.area?.city?.name ||
        property?.areaCityName ||
        '';

      if (String(explicitCity || '').trim()) {
        return String(explicitCity).trim();
      }

      const parsedCityFromPath = this.getCityFromPropertyPath(property?.path);

      if (parsedCityFromPath) {
        return parsedCityFromPath;
      }

      return '';
    },
    getCityFromPropertyPath(path) {
      if (!path) {
        return '';
      }

      const pathValue = String(path || '').trim();
      if (!pathValue) {
        return '';
      }

      try {
        const baseUrl =
          typeof window !== 'undefined' && window.location
            ? window.location.origin
            : 'https://www.unihomes.co.uk';
        const parsed = new URL(pathValue, baseUrl);
        const segments = parsed.pathname.split('/').filter(Boolean);
        const citySlugIndex = segments.indexOf('student-accommodation');

        if (citySlugIndex === -1 || !segments[citySlugIndex + 1]) {
          return '';
        }

        const citySlug = segments[citySlugIndex + 1];
        return citySlug
          .split('-')
          .map((part) =>
            part.length ? part.charAt(0).toUpperCase() + part.slice(1) : ''
          )
          .join(' ')
          .trim();
      } catch (_error) {
        return '';
      }
    },
    getEnquiryThumbnail(property) {
      if (!property) {
        return null;
      }

      return (
        (typeof property?.mainImage === 'string' ? property.mainImage : null) ||
        property?.mainImage?.src?.medium?.url ||
        property?.mainImage?.src?.small?.url ||
        property?.mainImage?.src?.tablet?.url ||
        property?.main_image_tablet_url ||
        property?.mainImageTabletUrl ||
        property?.defaultImage ||
        null
      );
    },
    setEnquiryModalThumbnails(enquiryProperties = []) {
      if (typeof document === 'undefined') {
        return;
      }

      const thumbnailItems = Array.from(
        document.querySelectorAll('[data-enquiry-thumbnail-item]')
      );

      const thumbnailImages = Array.from(
        document.querySelectorAll('[data-enquiry-thumbnail-image]')
      );

      const singleThumbnail = document.getElementById(
        'enquiry_multi_single_thumbnail'
      );

      const properties = Array.isArray(enquiryProperties)
        ? enquiryProperties
        : [];
      const thumbnails = properties
        .map((property) => this.getEnquiryThumbnail(property))
        .filter(Boolean);

      if (thumbnailItems.length > 0) {
        thumbnailItems.forEach((item, index) => {
          const image = thumbnailImages[index];
          const thumbnail = thumbnails[index] || null;
          const shouldShow = index < properties.length;

          if (shouldShow) {
            item.classList.remove('tw-hidden');

            if (image && thumbnail) {
              image.setAttribute('src', thumbnail);
            }

            return;
          }

          item.classList.add('tw-hidden');
        });
      }

      if (singleThumbnail && thumbnails[0]) {
        singleThumbnail.setAttribute('src', thumbnails[0]);
      }
    },
    async handleEnquireToUpvoted() {
      if (!this.enableHousemateGroups) {
        return;
      }

      if (this.isEnquireToUpvotedDisabled) {
        return;
      }

      const majoritySupportedProperties = this.enquirableUpvotedProperties;

      if (majoritySupportedProperties.length === 0) {
        return;
      }

      const propertyIds = majoritySupportedProperties
        .map((property) => this.getPropertyIdentifier(property))
        .filter((id) => Boolean(id))
        .filter((id, index, ids) => ids.indexOf(id) === index);

      const params = new URLSearchParams({
        'open-enquiry-modal': '1',
        'enquiry-context': 'housemate_group',
        'housemate-group-id': String(this.targetGroupId || ''),
        'housemate-group-property-ids': propertyIds.join(','),
      });
      const fallbackUrl = `/student/shortlist?${params.toString()}`;

      this.showMenuOptions = false;

      this.setEnquiryModalContext({
        context: 'housemate_group',
        housemateGroupId: this.targetGroupId,
        propertyIds,
      });

      this.setEnquiryModalSummary(majoritySupportedProperties);

      this.openEnquiryModal(fallbackUrl);
    },
    async handleHousemateGroupVote({ propertyId, vote } = {}) {
      const housemateGroupId = this.targetGroupId;

      if (!housemateGroupId || !propertyId || !['yes', 'no'].includes(vote)) {
        return;
      }

      const payload =
        await this.studentStore.voteHousemateGroupShortlistProperty({
          housemateGroupId,
          propertyId,
          vote,
        });

      if (!payload || payload.success !== true) {
        return;
      }
    },
    toggleFloatingMenuListeners(shouldEnable) {
      if (typeof document === 'undefined') {
        return;
      }

      if (shouldEnable && !this.floatingMenuListenersActive) {
        document.addEventListener(
          'mousedown',
          this.handleOutsideFloatingMenuPointerDown,
          true
        );
        document.addEventListener(
          'touchstart',
          this.handleOutsideFloatingMenuPointerDown,
          true
        );
        document.addEventListener('keydown', this.handleFloatingMenuKeyDown);
        this.floatingMenuListenersActive = true;
        return;
      }

      if (!shouldEnable && this.floatingMenuListenersActive) {
        document.removeEventListener(
          'mousedown',
          this.handleOutsideFloatingMenuPointerDown,
          true
        );
        document.removeEventListener(
          'touchstart',
          this.handleOutsideFloatingMenuPointerDown,
          true
        );
        document.removeEventListener('keydown', this.handleFloatingMenuKeyDown);
        this.floatingMenuListenersActive = false;
      }
    },

    handleOutsideFloatingMenuPointerDown(event) {
      if (!this.showMenuOptions) {
        return;
      }

      const wrapper = this.$refs.floatingMenuOptionsWrapper;
      const target = event?.target;

      if (!wrapper || !target) {
        return;
      }

      if (wrapper.contains(target)) {
        return;
      }

      this.showMenuOptions = false;
    },

    handleFloatingMenuKeyDown(event) {
      if (!this.showMenuOptions) {
        return;
      }

      if (event?.key === 'Escape') {
        this.showMenuOptions = false;
      }
    },

    getMemberStudentId(member) {
      return member?.student_id ?? member?.id;
    },
    setMemberActionLoading(memberStudentId, isLoading) {
      if (!memberStudentId) return;

      const id = String(memberStudentId);
      const current = Array.isArray(this.memberActionLoadingStudentIds)
        ? this.memberActionLoadingStudentIds
        : [];

      if (isLoading) {
        if (current.includes(id)) return;
        this.memberActionLoadingStudentIds = [...current, id];
        return;
      }

      if (!current.includes(id)) return;
      this.memberActionLoadingStudentIds = current.filter(
        (existingId) => existingId !== id
      );
    },
    setMobileManageScrollLock(locked) {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      const html = document.documentElement;
      const body = document.body;

      if (locked) {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (!isMobile) {
          return;
        }

        if (this.isMobileManageScrollLocked) {
          return;
        }

        this.previousHtmlOverflow = html.style.overflow;
        this.previousBodyOverflow = body.style.overflow;

        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        this.isMobileManageScrollLocked = true;
        return;
      }

      if (!this.isMobileManageScrollLocked) {
        return;
      }

      html.style.overflow = this.previousHtmlOverflow ?? '';
      body.style.overflow = this.previousBodyOverflow ?? '';
      this.previousHtmlOverflow = null;
      this.previousBodyOverflow = null;
      this.isMobileManageScrollLocked = false;
    },
    async copyShareUrlToClipboard(url) {
      if (!url) {
        return false;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }

      if (typeof document === 'undefined') {
        return false;
      }

      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);

      return copied;
    },
    async handleManageShare() {
      if (!this.manageShareUrl) {
        this.manageShareLabel = 'Error!';
        return;
      }

      try {
        let shared = false;

        if (navigator.share) {
          try {
            await navigator.share({
              title: 'UniHomes',
              text: `Join my housemate group using this link:\n${this.manageShareUrl}`,
              url: this.manageShareUrl,
            });
            shared = true;
          } catch (err) {
            // Fall through to clipboard fallback when native share is unavailable/blocked.
            void err;
          }
        }

        if (!shared) {
          const copied = await this.copyShareUrlToClipboard(
            this.manageShareUrl
          );
          if (copied) {
            this.manageShareLabel = 'Link copied';
          } else {
            this.manageShareLabel = 'Error!';
          }
        }
      } catch (err) {
        this.manageShareLabel = 'Error!';
        void err;
      } finally {
        if (this.manageShareResetTimeoutId) {
          clearTimeout(this.manageShareResetTimeoutId);
        }

        this.manageShareResetTimeoutId = setTimeout(() => {
          this.manageShareLabel = 'Share link to invite housemates';
        }, 3000);
      }
    },
    async handleAfterGroupDeleted() {
      const nextGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!nextGroupId) {
        this.studentStore.showHousemateGroupDetail = false;
        this.studentStore.setMenuHeaderText('grouplist');
        return;
      }

      this.studentStore.showHousemateGroupDetail = true;

      await this.studentStore.getHousemateGroupShortlists(nextGroupId);

      const count = Number.isFinite(
        this.studentStore.housemateGroupShortlistCount
      )
        ? this.studentStore.housemateGroupShortlistCount
        : Array.isArray(this.studentStore.housemateGroupShortlists)
        ? this.studentStore.housemateGroupShortlists.length
        : 0;

      this.studentStore.setMenuHeaderText(
        this.studentStore.targetHousemateGroup?.housemate_group_name ??
          'Grouplist',
        count
      );
    },
    async confirmDeleteGroupFromMenu() {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!housemateGroupId) {
        return;
      }

      this.isManagingGroup = true;
      const success = await this.studentStore.deleteHousemateGroup({
        housemateGroupId,
      });
      this.isManagingGroup = false;

      if (success) {
        this.showManageGroup = false;
        this.showMenuOptions = false;
        await this.handleAfterGroupDeleted();
      }
    },
    async handleImmediateDeleteGroup() {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!housemateGroupId) {
        return;
      }

      this.isManagingGroup = true;
      const success = await this.studentStore.deleteHousemateGroup({
        housemateGroupId,
      });
      this.isManagingGroup = false;

      if (success) {
        this.showGroupDeleteModal = false;
        this.showManageGroup = false;
        this.showMenuOptions = false;
        await this.handleAfterGroupDeleted();
      }
    },
    async openContext(context) {
      if (context === 'change_group') {
        this.showMenuOptions = false;

        if (!this.studentStore.housemateGroupShortlistedByPropertyIdFetched) {
          await this.studentStore
            .getHousemateGroupShortlistedPropertyIds?.()
            .catch(() => null);
        }

        this.showGroupListModal = true;
      }

      if (context === 'create_group') {
        this.showMenuOptions = false;
        this.showGroupCreateModal = true;
      }

      if (context === 'manage_group') {
        this.showMenuOptions = false;
        this.showManageGroup = true;
      }

      if (context === 'rename_group') {
        this.showMenuOptions = false;
        this.handleRenameGroup();
      }
    },
    closeManageGroup() {
      this.showManageGroup = false;
    },
    async handleApproveMember(member) {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      const memberStudentId = this.getMemberStudentId(member);

      if (!housemateGroupId || !memberStudentId) {
        return;
      }

      this.setMemberActionLoading(memberStudentId, true);
      try {
        await this.studentStore.approveHousemateGroupMember({
          housemateGroupId,
          memberStudentId,
        });
      } finally {
        this.setMemberActionLoading(memberStudentId, false);
      }
    },
    async handleDeclineMember(member) {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      const memberStudentId = this.getMemberStudentId(member);

      if (!housemateGroupId || !memberStudentId) {
        return;
      }

      this.setMemberActionLoading(memberStudentId, true);
      try {
        await this.studentStore.declineHousemateGroupMember({
          housemateGroupId,
          memberStudentId,
        });
      } finally {
        this.setMemberActionLoading(memberStudentId, false);
      }
    },
    async handleLeaveGroup() {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!housemateGroupId) {
        return;
      }

      this.isManagingGroup = true;
      const success = await this.studentStore.leaveHousemateGroup({
        housemateGroupId,
      });
      this.isManagingGroup = false;

      if (success) {
        this.showManageGroup = false;
        this.showMenuOptions = false;
        this.studentStore.showHousemateGroupDetail = false;
        this.studentStore.setMenuHeaderText('grouplist');
      }
    },
    async handleRemoveMember(member) {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      const memberStudentId = this.getMemberStudentId(member);

      if (!housemateGroupId || !memberStudentId) {
        return;
      }

      this.setMemberActionLoading(memberStudentId, true);
      try {
        await this.studentStore.removeHousemateGroupMember({
          housemateGroupId,
          memberStudentId,
        });
      } finally {
        this.setMemberActionLoading(memberStudentId, false);
      }
    },
    handleRenameGroup() {
      this.studentStore.clearManageError();
      this.renameGroupName =
        this.studentStore.targetHousemateGroup?.housemate_group_name ?? '';
      this.showGroupRenameModal = true;
    },
    closeGroupRenameModal() {
      this.showGroupRenameModal = false;
      this.studentStore.clearManageError();
    },
    async submitGroupRename() {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!housemateGroupId) {
        return;
      }

      this.isManagingGroup = true;
      const result = await this.studentStore.postRenameHousemateGroup({
        housemateGroupId,
        name: this.renameGroupName,
      });
      this.isManagingGroup = false;

      if (result) {
        this.showGroupRenameModal = false;
      }
    },
    handleDeleteGroup() {
      this.studentStore.clearManageError();
      this.showGroupDeleteModal = true;
    },
    closeGroupDeleteModal() {
      this.showGroupDeleteModal = false;
      this.studentStore.clearManageError();
    },
    async confirmGroupDelete() {
      const housemateGroupId =
        this.studentStore.targetHousemateGroup?.housemate_group_id;

      if (!housemateGroupId) {
        return;
      }

      this.isManagingGroup = true;
      const success = await this.studentStore.deleteHousemateGroup({
        housemateGroupId,
      });
      this.isManagingGroup = false;

      if (success) {
        this.showGroupDeleteModal = false;
        this.showManageGroup = false;
        this.showMenuOptions = false;
        this.studentStore.showHousemateGroupDetail = false;
        this.studentStore.setMenuHeaderText('grouplist');
      }
    },
    closeGroupListModal() {
      this.showGroupListModal = false;
      this.showToolTip = false;
    },
    async createHousemateGroup() {
      this.showToolTip = false;
      this.isCreatingNewHousemateGroup = true;

      try {
        const selectedCityId = this.selectedLocation?.cityId ?? null;
        const selectedUniversityId =
          this.selectedLocation?.universityId ?? null;

        await this.studentStore.postHousemateGroup(
          this.inputGroupName,
          selectedCityId,
          selectedUniversityId
        );

        if (!this.studentStore.hasGroupCreateError) {
          this.showGroupCreateModal = false;
          this.studentStore.showHousemateGroupDetail = true;
          this.inputGroupName = this.studentStore.nextGroupName;
          this.selectedLocation = null;
        }
      } finally {
        this.isCreatingNewHousemateGroup = false;
      }
    },
    closeGroupCreateModal() {
      this.inputGroupName = this.studentStore.nextGroupName;
      this.selectedLocation = null;
      this.showGroupCreateModal = false;
      this.showToolTip = false;
      this.studentStore.clearHousemateGroupError();
    },
    handleShowToolTip() {
      if (!this.isCreatingNewHousemateGroup) {
        this.showToolTip = true;

        if (this.showToolTipTimeout) {
          clearTimeout(this.showToolTipTimeout);
        }

        this.showToolTipTimeout = setTimeout(
          () => (this.showToolTip = false),
          3000
        );
      }
    },
    handleCloseToolTip() {
      if (this.showToolTipTimeout) {
        clearTimeout(this.showToolTipTimeout);
      }

      this.showToolTipTimeout = setTimeout(
        () => (this.showToolTip = false),
        3000
      );
    },
    refreshGroupManagementData() {
      if (!this.studentStore.isRefreshingManageGroup) {
        this.studentStore.handleHousemateDataRefresh();
      }
    },
  },
};
</script>
